import React, { useState, useEffect } from 'react';
import { LocalMatchday, CreateLocalMatchdayRequest, UpdateLocalMatchdayRequest } from '../../types/localMatchday';

interface LocalMatchdayFormProps {
  localMatchday?: LocalMatchday;
  onSubmit: (data: CreateLocalMatchdayRequest | UpdateLocalMatchdayRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const LocalMatchdayForm: React.FC<LocalMatchdayFormProps> = ({
  localMatchday,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    organizer: '',
    expectedAttendees: '',
    actualAttendees: '',
    notes: '',
    isActive: true,
    isCompleted: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (localMatchday) {
      const date = new Date(localMatchday.date);
      const dateString = date.toISOString().split('T')[0];
      
      setFormData({
        name: localMatchday.name || '',
        location: localMatchday.location || '',
        description: localMatchday.description || '',
        date: dateString,
        startTime: localMatchday.startTime ? new Date(localMatchday.startTime).toTimeString().slice(0, 5) : '',
        endTime: localMatchday.endTime ? new Date(localMatchday.endTime).toTimeString().slice(0, 5) : '',
        organizer: localMatchday.organizer || '',
        expectedAttendees: localMatchday.expectedAttendees?.toString() || '',
        actualAttendees: localMatchday.actualAttendees?.toString() || '',
        notes: localMatchday.notes || '',
        isActive: localMatchday.isActive,
        isCompleted: localMatchday.isCompleted
      });
    }
  }, [localMatchday]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.date) {
      newErrors.date = 'Datum ist erforderlich';
    }

    if (formData.expectedAttendees && isNaN(Number(formData.expectedAttendees))) {
      newErrors.expectedAttendees = 'Erwartete Teilnehmer muss eine Zahl sein';
    }

    if (formData.actualAttendees && isNaN(Number(formData.actualAttendees))) {
      newErrors.actualAttendees = 'Tatsächliche Teilnehmer muss eine Zahl sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const baseData = {
        name: formData.name.trim(),
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined,
        date: formData.date,
        startTime: formData.startTime ? `${formData.date}T${formData.startTime}:00` : undefined,
        endTime: formData.endTime ? `${formData.date}T${formData.endTime}:00` : undefined,
        organizer: formData.organizer.trim() || undefined,
        expectedAttendees: formData.expectedAttendees ? Number(formData.expectedAttendees) : undefined,
        notes: formData.notes.trim() || undefined,
        isActive: formData.isActive
      };

      if (localMatchday) {
        // Update request
        const updateData: UpdateLocalMatchdayRequest = {
          ...baseData,
          actualAttendees: formData.actualAttendees ? Number(formData.actualAttendees) : undefined,
          isCompleted: formData.isCompleted
        };
        await onSubmit(updateData);
      } else {
        // Create request
        const createData: CreateLocalMatchdayRequest = baseData;
        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2>{localMatchday ? 'Lokalen Spieltag bearbeiten' : 'Neuen lokalen Spieltag erstellen'}</h2>
          <button onClick={onCancel} style={closeButtonStyle}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="z.B. Derby-Wochenende"
              />
              {errors.name && <span style={errorStyle}>{errors.name}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Ort</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="z.B. Sportpark Lokalverein"
              />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              style={textareaStyle}
              placeholder="Beschreibung des Spieltags..."
              rows={3}
            />
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Datum *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.date && <span style={errorStyle}>{errors.date}</span>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Startzeit</label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Endzeit</label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Organisator</label>
              <input
                type="text"
                name="organizer"
                value={formData.organizer}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="z.B. FC Lokalverein"
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Erwartete Teilnehmer</label>
              <input
                type="number"
                name="expectedAttendees"
                value={formData.expectedAttendees}
                onChange={handleInputChange}
                style={inputStyle}
                min="0"
                placeholder="z.B. 500"
              />
              {errors.expectedAttendees && <span style={errorStyle}>{errors.expectedAttendees}</span>}
            </div>

            {localMatchday && (
              <div style={fieldStyle}>
                <label style={labelStyle}>Tatsächliche Teilnehmer</label>
                <input
                  type="number"
                  name="actualAttendees"
                  value={formData.actualAttendees}
                  onChange={handleInputChange}
                  style={inputStyle}
                  min="0"
                  placeholder="z.B. 450"
                />
                {errors.actualAttendees && <span style={errorStyle}>{errors.actualAttendees}</span>}
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Notizen</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              style={textareaStyle}
              placeholder="Zusätzliche Notizen..."
              rows={3}
            />
          </div>

          <div style={rowStyle}>
            <div style={checkboxFieldStyle}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={checkboxStyle}
              />
              <label style={checkboxLabelStyle}>Aktiv</label>
            </div>

            {localMatchday && (
              <div style={checkboxFieldStyle}>
                <input
                  type="checkbox"
                  name="isCompleted"
                  checked={formData.isCompleted}
                  onChange={handleInputChange}
                  style={checkboxStyle}
                />
                <label style={checkboxLabelStyle}>Abgeschlossen</label>
              </div>
            )}
          </div>

          <div style={buttonRowStyle}>
            <button type="button" onClick={onCancel} style={cancelButtonStyle}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading} style={submitButtonStyle}>
              {loading ? 'Speichern...' : (localMatchday ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
  borderBottom: '1px solid #e5e7eb',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#6b7280',
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  marginBottom: '1rem',
};

const fieldStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
};

const labelStyle: React.CSSProperties = {
  marginBottom: '0.25rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '0.875rem',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const checkboxFieldStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const checkboxStyle: React.CSSProperties = {
  width: '1rem',
  height: '1rem',
};

const checkboxLabelStyle: React.CSSProperties = {
  fontWeight: '500',
  color: '#374151',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '4px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: '0.25rem',
};

export default LocalMatchdayForm;
